import React, { useCallback, useState, useRef } from "react";
import { ContentItem } from "../../content-card/types";
import { CardPosition, DragState } from "./useDragPosition";
import ContentCard from "../../content-card/ContentCard";
import SelectionCheckbox from "../../content-card/SelectionCheckbox";

interface DraggableCardProps {
  item: ContentItem;
  position: CardPosition;
  dragState: DragState;
  onMouseDown: (cardId: string, event: React.MouseEvent) => void;
  onContentClick: (id: string) => void;
  onDeleteContent: (id: string) => void;
  onEditContent?: (id: string, currentTitle: string) => void;
  onArchiveContent?: (id: string) => void;
  allContentIds: string[];
  cardWidth: number;
  cardHeight: number;
  isSelected?: boolean;
  onToggleSelection?: (id: string, multiSelect?: boolean, rangeSelect?: boolean) => void;
}

const DraggableCard = ({
  item,
  position,
  dragState,
  onMouseDown,
  onContentClick,
  onDeleteContent,
  onEditContent,
  onArchiveContent,
  allContentIds,
  cardWidth,
  cardHeight,
  isSelected = false,
  onToggleSelection
}: DraggableCardProps) => {
  const isDragging = dragState.isDragging && dragState.draggedCardIds.includes(item.id);
  
  // Track drag state to prevent unwanted clicks
  const [hasDragged, setHasDragged] = useState(false);
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);
  const DRAG_THRESHOLD = 5; // pixels - same as Grid view uses
  
  // Handle mouse down for dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't start drag if clicking on checkbox or other interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('[data-selection-checkbox]') || target.closest('button')) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    // Store initial mouse position
    mouseDownPos.current = { x: e.clientX, y: e.clientY };
    setHasDragged(false);
    
    onMouseDown(item.id, e);
  }, [item.id, onMouseDown]);

  // Handle mouse move to detect dragging
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (mouseDownPos.current && !hasDragged) {
      const deltaX = Math.abs(e.clientX - mouseDownPos.current.x);
      const deltaY = Math.abs(e.clientY - mouseDownPos.current.y);
      
      if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
        setHasDragged(true);
      }
    }
  }, [hasDragged, DRAG_THRESHOLD]);

  // Handle mouse up - reset drag state
  const handleMouseUp = useCallback(() => {
    mouseDownPos.current = null;
    // Reset hasDragged after a short delay to prevent immediate clicks
    setTimeout(() => setHasDragged(false), 100);
  }, []);

  // Handle selection checkbox click
  const handleSelectionClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onToggleSelection) {
      const multiSelect = e.ctrlKey || e.metaKey;
      const rangeSelect = e.shiftKey;
      onToggleSelection(item.id, multiSelect, rangeSelect);
    }
  }, [item.id, onToggleSelection]);

  // Custom click handlers that respect drag state
  const handleThumbnailClick = useCallback((e: React.MouseEvent) => {
    // Prevent clicks during or after dragging
    if (hasDragged || isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // Stop event propagation to prevent the card's onClick from firing
    e.preventDefault();
    e.stopPropagation();
    
    console.log(`[NAVIGATION DEBUG] DraggableCard - Thumbnail clicked for item ${item.id}, opening URL: ${item.url}`);
    // Open in new tab without triggering card click
    window.open(item.url, "_blank");
  }, [hasDragged, isDragging, item.id, item.url]);

  const handleLabelClick = useCallback((e: React.MouseEvent) => {
    // Prevent clicks during or after dragging
    if (hasDragged || isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    console.log(`[NAVIGATION DEBUG] DraggableCard - Label clicked for item ${item.id}, calling onContentClick`);
    onContentClick(item.id);
  }, [hasDragged, isDragging, item.id, onContentClick]);

  return (
    <div
      data-content-card={item.id}
      className={`absolute pointer-events-auto transition-transform duration-200 ${
        isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab'
      } ${isSelected ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}
      style={{
        left: position.x,
        top: position.y,
        width: cardWidth,
        height: cardHeight,
        zIndex: position.zIndex,
        transform: isDragging ? 'scale(1.05)' : 'scale(1)',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={(e) => {
        // Prevent any clicks during or after dragging
        if (hasDragged || isDragging) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }}
    >
      {/* Selection checkbox */}
      {onToggleSelection && (
        <SelectionCheckbox
          isSelected={isSelected}
          onClick={handleSelectionClick}
          className="absolute top-2 left-2 z-30"
        />
      )}
      
      {/* Content card with custom click handlers */}
      <div className="w-full h-full">
        <ContentCard
          item={item}
          onDelete={onDeleteContent}
          onEdit={onEditContent}
          onArchive={onArchiveContent}
          view="grid"
          allContentIds={allContentIds}
          // Pass custom click handlers that respect drag state
          onThumbnailClick={handleThumbnailClick}
          onLabelClick={handleLabelClick}
        />
      </div>
      
      {/* Selection overlay for visual feedback */}
      {isSelected && (
        <div className="absolute inset-0 bg-blue-500 bg-opacity-10 rounded-lg pointer-events-none" />
      )}
    </div>
  );
};

export default DraggableCard;
